import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { getPublicUrl } from "src/config/site";

export default function SEO({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogType,
  ogUrl,
  ogImage,
  type,
  url,
  image,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
  robots,
}) {
  const location = useLocation();

  const routeSeoConfig = {
    "/": {
      title: "WatchNod - Server Health & API Monitoring",
      description: "Real-time server health and API monitoring with uptime tracking, alerts, customizable dashboards, and actionable metrics.",
      keywords: "server monitoring, api monitoring, uptime tracking, server health",
    },
    "/document": {
      title: "WatchNod Documentation | Server Monitoring Setup & Guide",
      description: "Explore WatchNod documentation to learn server monitoring setup, performance tracking, uptime monitoring, and alerts for reliable infrastructure management.",
      keywords: "documentation, setup guide, server monitoring help",
    },
    "/blog": {
      title: "WatchNod Blog | Server, API & Uptime Monitoring Guides",
      description: "Explore WatchNod blog for guides on server monitoring, API monitoring, uptime tracking, and tips to improve performance and reliability.",
    },
    "/about": {
      title: "About WatchNod | Server & API Monitoring Platform",
      description: "Learn about WatchNod, a reliable server and API monitoring platform offering real-time alerts, uptime tracking, and scalable solutions to keep your systems running.",
    },
    "/contact": {
      title: "Contact WatchNod | Server & API Monitoring Support",
      description: "Contact WatchNod for support, queries, or help with server monitoring and API monitoring. We're here to keep your systems reliable and running.",
    },
    "/faq": {
      title: "WatchNod FAQs | Server, API & Domain Monitoring Help",
      description: "Find answers to common questions about server, API, and domain monitoring, plans, setup, and alerts to manage your infrastructure efficiently.",
    },
    "/testimonial": {
      title: "WatchNod Reviews & Testimonials | Trusted Monitoring Solution",
      description: "Read WatchNod testimonials to see how businesses use server, API, and uptime monitoring to improve performance, prevent downtime, and scale reliably.",
    },
    "/case-studies": {
      title: "WatchNod Case Studies | Monitoring Success Stories",
      description: "Explore WatchNod case studies to see how businesses prevent downtime, improve uptime, and manage server, API, and domain monitoring effectively.",
    },
    "/privacy-policy": {
      title: "Privacy Policy | WatchNod Data Protection & Security",
      description: "Read WatchNod privacy policy to understand how we collect, use, and protect your data, including server metrics, account information, and security practices.",
    },
    "/terms-and-conditions": {
      title: "Terms & Conditions | WatchNod Usage & Service Terms",
      description: "Read WatchNod terms and conditions to understand service usage, user responsibilities, licensing, cookies, and policies for using our monitoring platform.",
    },
    "/service-delivery-policy": {
      title: "Service Delivery Policy | WatchNod Subscription & Access",
      description: "Read WatchNod service delivery policy to understand subscription activation, instant access, account setup, cancellations, and support details.",
    },
    "/login": {
      title: "WatchNod Login | Access Your Monitoring Dashboard",
      description: "Login to WatchNod to access your server, API, and domain monitoring dashboard, track uptime, and manage alerts in one place.",
    },
    "/signup": {
      title: "WatchNod Sign Up | Start Server & API Monitoring",
      description: "Create your WatchNod account to start monitoring servers, APIs, and domains with real-time alerts, uptime tracking, and easy setup.",
    },
  };

  const DEFAULT_TITLE = "WatchNod - Server Health & API Monitoring";
  const DEFAULT_DESCRIPTION = "Real-time server health and API monitoring with uptime tracking, alerts, customizable dashboards, and actionable metrics.";
  const DEFAULT_KEYWORDS = "server monitoring, api monitoring, uptime tracking, alerts, dashboards";

  const normalizedPathname = location.pathname === "/" ? "/" : location.pathname.replace(/\/+$/, "");

  const getSeoPath = () => {
    if (normalizedPathname.startsWith("/blog/")) return "/blog/:slug";
    if (normalizedPathname.startsWith("/case-studies/")) return "/case-studies/:slug";
    return normalizedPathname;
  };

  const seoPath = getSeoPath();
  const pageSeo = routeSeoConfig[seoPath];

  const finalTitle = title || pageSeo?.title || DEFAULT_TITLE;
  const finalDescription = description || pageSeo?.description || DEFAULT_DESCRIPTION;
  const finalKeywords = keywords || pageSeo?.keywords || DEFAULT_KEYWORDS;
  const finalOgTitle = ogTitle || finalTitle;
  const finalOgDescription = ogDescription || finalDescription;
  const finalOgType = ogType || type || "website";
  const finalOgUrl = ogUrl || getPublicUrl(url || (normalizedPathname === "/" ? "" : normalizedPathname));
  const finalOgImage = ogImage || (image ? getPublicUrl(image) : getPublicUrl("/Logo/watch-nod.png"));
  const finalTwitterCard = twitterCard || "summary_large_image";
  const finalTwitterTitle = twitterTitle || finalOgTitle;
  const finalTwitterDescription = twitterDescription || finalOgDescription;
  const finalTwitterImage = twitterImage || finalOgImage;

  const isPrivatePath = normalizedPathname.startsWith("/dashboard") || normalizedPathname.startsWith("/login") || normalizedPathname.startsWith("/signup");
  const finalRobots = robots || (isPrivatePath ? "noindex, nofollow" : "index, follow");

  useEffect(() => {
    document.title = finalTitle;

    const setMeta = (name, content, attr = 'name') => {
      if (!content) return;
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };

    setMeta('description', finalDescription);
    setMeta('keywords', finalKeywords);
    setMeta('robots', finalRobots);

    // Open Graph
    setMeta('og:type', finalOgType, 'property');
    setMeta('og:site_name', 'WatchNod', 'property');
    setMeta('og:url', finalOgUrl, 'property');
    setMeta('og:title', finalOgTitle, 'property');
    setMeta('og:description', finalOgDescription, 'property');
    setMeta('og:image', finalOgImage, 'property');

    // Twitter
    setMeta('twitter:card', finalTwitterCard, 'name');
    setMeta('twitter:url', finalOgUrl, 'name');
    setMeta('twitter:title', finalTwitterTitle, 'name');
    setMeta('twitter:description', finalTwitterDescription, 'name');
    setMeta('twitter:image', finalTwitterImage, 'name');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', finalOgUrl);

    const noscript = document.getElementById('page-fallback');
    if (noscript) {
      noscript.innerHTML = `<div style="font-size: 2em; font-weight: bold; margin-bottom: 20px;">${finalTitle}</div><div>${finalDescription}</div>`;
    }
  }, [finalTitle, finalDescription, finalKeywords, finalOgTitle, finalOgDescription, finalOgType, finalOgUrl, finalOgImage, finalTwitterCard, finalTwitterTitle, finalTwitterDescription, finalTwitterImage, finalRobots]);

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content={finalRobots} />
      <link rel="canonical" href={finalOgUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={finalOgType} />
      <meta property="og:site_name" content="WatchNod" />
      <meta property="og:url" content={finalOgUrl} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={finalOgImage} />

      {/* Twitter */}
      <meta name="twitter:card" content={finalTwitterCard} />
      <meta name="twitter:url" content={finalOgUrl} />
      <meta name="twitter:title" content={finalTwitterTitle} />
      <meta name="twitter:description" content={finalTwitterDescription} />
      <meta name="twitter:image" content={finalTwitterImage} />
    </Helmet>
  );
}
